'use client'

import { Zap } from 'lucide-react'
import type { FuelEstimate } from '@/lib/types'
import { type AuxPowerLine, useAuxPowerLine } from '@/stores/use-aux-power-line'

interface CostSummaryProps {
	estimate: FuelEstimate
	/** ms elapsed on the most recent Calculate (passed from the store) */
	calculationMs: number | null
	/**
	 * Always false in the current build — the shared route-level cache was
	 * removed 2026-04-18 (Codex Critical #2). Kept on the prop surface so
	 * the store→component contract doesn't churn; can be deleted in a
	 * follow-up cleanup if no consumer ever needs it again.
	 */
	wasCached: boolean
}

/**
 * Pretty-format the price-freshness date. EIA publishes weekly, so a
 * fetchedAt timestamp approximates the data's "as of" date. We show
 * relative form for recent (today/yesterday), absolute for older.
 */
function formatPriceDate(ageMs: number): string {
	const date = new Date(Date.now() - ageMs)
	const now = new Date()
	const sameDay = date.toDateString() === now.toDateString()
	if (sameDay) {
		return 'today'
	}
	const yesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000)
	if (date.toDateString() === yesterday.toDateString()) {
		return 'yesterday'
	}
	return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

/**
 * Format a dollar amount with 0 or 2 decimals depending on magnitude.
 * Under $100 we keep cents (pleasantly precise for short hops). Over $100
 * we drop them — the range is already ± ~$10, the pennies are noise.
 */
function formatUsd(amount: number): string {
	const opts =
		amount < 100
			? { minimumFractionDigits: 2, maximumFractionDigits: 2 }
			: { minimumFractionDigits: 0, maximumFractionDigits: 0 }
	return amount.toLocaleString(undefined, opts)
}

/**
 * Compute the percentage delta vs preset for the MPG comparison line.
 * Returns `null` if there's no override (baseMpg === presetMpg).
 */
function mpgDeltaText(baseMpg: number, presetMpg: number): string | null {
	if (baseMpg === presetMpg) return null
	const deltaPct = Math.round(((baseMpg - presetMpg) / presetMpg) * 100)
	if (deltaPct === 0) return null
	return deltaPct > 0
		? `${deltaPct}% better than preset`
		: `${Math.abs(deltaPct)}% lower than preset`
}

export function CostSummary({ estimate, calculationMs, wasCached }: CostSummaryProps) {
	const hours = Math.floor(estimate.totalDurationMinutes / 60)
	const mins = estimate.totalDurationMinutes % 60
	/**
	 * A custom override is active when the base MPG used for the calculation
	 * differs from the vehicle's preset MPG. Show a small "custom" annotation
	 * so the user knows their override is what produced these numbers.
	 */
	const isCustomMpg = estimate.baseMpg !== estimate.presetMpg
	const deltaText = mpgDeltaText(estimate.baseMpg, estimate.presetMpg)
	const priceDate = formatPriceDate(estimate.warnings.fuelPricesAgeMs)
	// Generator/reefer add-on (null when off). Derived from this estimate, so
	// toggling it updates the total instantly without re-running the route.
	const aux = useAuxPowerLine()
	const total = estimate.totalCost + (aux?.cost ?? 0)

	return (
		<section className="relative overflow-hidden rounded-2xl border border-border bg-surface">
			{/* purple brand top rail — brand punch, scarce */}
			<div className="absolute top-0 inset-x-0 h-[2px] bg-brand" />

			{/* Hero stat — total cost + rough-variance range.
			    The ±5% band is a DISPLAY-ONLY heuristic to illustrate
			    real-world spread (station prices vary $0.20–$0.40/gal within
			    a state, driving style adds more). It is NOT a validated
			    accuracy claim — the estimator has not been calibrated
			    against fleet receipt data. Codex Medium #1 follow-up
			    2026-04-19 + Codex Low follow-up 2026-04-19 (wording drift):
			    keep the label non-authoritative ("Rough range" not
			    "Typical range" which reads as a seen-patterns claim) and
			    resist any future marketing copy that cites the 5% as a
			    proven accuracy number until we have real backtesting. */}
			<div className="px-6 pt-7 pb-6 text-center">
				<p className="font-mono text-[10px] uppercase tracking-[0.28em] text-text-muted">
					Total Fuel Cost · Estimated
				</p>
				<p className="mt-3 font-mono text-5xl sm:text-6xl font-light tabular-nums text-text-primary tracking-tight leading-none">
					<span className="text-brand">$</span>
					{total.toLocaleString(undefined, {
						minimumFractionDigits: 2,
						maximumFractionDigits: 2,
					})}
				</p>
				<p
					className="mt-4 font-mono text-[10px] uppercase tracking-[0.18em] text-text-muted tabular-nums"
					title="Rough range only — illustrative of spread from station-level price variance within each state plus driving style. Not a validated accuracy band."
				>
					Rough Range · ${formatUsd(total * 0.95)} — ${formatUsd(total * 1.05)}
				</p>
				{/* Responsibility disclaimer — tiny, linked to /terms. This is the
				    first decision-moment where a user might take our number and
				    act on it, so the disclosure lives here rather than only in
				    the footer. See docs/FUEL_APP.md legal section. */}
				<p className="mt-2 text-[10px] italic text-text-muted">
					Estimate only —{' '}
					<a
						href="/terms"
						target="_blank"
						rel="noopener"
						className="underline decoration-dotted underline-offset-2 hover:text-text-primary hover:decoration-solid"
					>
						see terms
					</a>
				</p>
			</div>

			{/* Secondary stats — 4-column hairline-divided strip */}
			<div className="grid grid-cols-4 divide-x divide-border border-t border-border">
				<Stat label="Distance" value={estimate.totalDistanceMiles.toLocaleString()} unit="mi" />
				<Stat label="Drive" value={`${hours}h ${mins}m`} />
				<Stat label="Gallons" value={estimate.totalGallons.toLocaleString()} unit="gal" />
				<Stat label="Eff. MPG" value={`${estimate.averageEffectiveMpg}`} />
			</div>

			{/* Cost breakdown — only when an add-on contributes to the total */}
			{aux && (
				<div className="space-y-1 border-t border-border px-5 py-3 font-mono text-xs tabular-nums text-text-secondary">
					<div className="flex justify-between gap-3">
						<span>Driving</span>
						<span className="whitespace-nowrap">
							{estimate.totalGallons.toLocaleString()} gal · ${estimate.totalCost.toFixed(2)}
						</span>
					</div>
					<div>
						<div className="flex justify-between gap-3">
							<span>{aux.label}</span>
							<span className="whitespace-nowrap">
								{aux.gallons} gal · ${aux.cost.toFixed(2)}
							</span>
						</div>
						<div className="text-[10px] text-text-muted">{auxDetail(aux)}</div>
					</div>
				</div>
			)}

			{/* Custom MPG annotation — only when override is active */}
			{isCustomMpg && (
				<div className="border-t border-border px-5 py-3 text-center">
					<p className="font-mono text-[10px] uppercase tracking-[0.18em] text-text-muted tabular-nums">
						Custom MPG · {estimate.baseMpg}
						<span className="text-text-muted/60"> · Preset {estimate.presetMpg}</span>
						{deltaText && (
							<>
								<span className="text-text-muted/60"> · </span>
								<span className="text-text-secondary normal-case tracking-normal font-sans">
									{deltaText}
								</span>
							</>
						)}
					</p>
				</div>
			)}

			{/* Meta row — pricing date + timing */}
			<div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 border-t border-border bg-background/40 px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.18em] text-text-muted">
				<span>Prices · {priceDate}</span>
				{calculationMs !== null && (
					<>
						<span aria-hidden>·</span>
						<span className="inline-flex items-center gap-1">
							{wasCached && <Zap className="size-2.5 text-warning" aria-hidden />}
							<span className="tabular-nums">
								{calculationMs < 1000
									? `${calculationMs}ms`
									: `${(calculationMs / 1000).toFixed(1)}s`}
							</span>
							{wasCached ? 'Cached' : 'Live'}
						</span>
					</>
				)}
			</div>
		</section>
	)
}

/** "4h 19m drive + 8h parked @ 0.75 gal/hr" (parked part only when set) */
function auxDetail(aux: AuxPowerLine): string {
	const parked = aux.idleHours > 0 ? ` + ${formatHours(aux.idleHours)} parked` : ''
	return `${formatHours(aux.driveHours)} drive${parked} @ ${aux.gallonsPerHour} gal/hr`
}

/** 4.3 h → "4h 18m", 8 h → "8h", 0.5 h → "30m" */
function formatHours(hours: number): string {
	const totalMinutes = Math.round(hours * 60)
	const h = Math.floor(totalMinutes / 60)
	const m = totalMinutes % 60
	if (h === 0) return `${m}m`
	return m === 0 ? `${h}h` : `${h}h ${m}m`
}

function Stat({ label, value, unit }: { label: string; value: string; unit?: string }) {
	return (
		<div className="px-3 py-4 text-center">
			<p className="font-mono text-[9px] uppercase tracking-[0.22em] text-text-muted">{label}</p>
			<p className="mt-2 font-mono text-xl font-light tabular-nums text-text-primary tracking-tight leading-none">
				{value}
				{unit && <span className="ml-1 text-xs text-text-muted">{unit}</span>}
			</p>
		</div>
	)
}
